"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.fetchOrderRecipients = exports.deleteAutomaticOrderTemplate = exports.fetchOrderFiles = exports.fetchOrder = exports.prevalidateImportedFiles = exports.prevalidateFiles = exports.createNewOrder = exports.changeStep = exports.validateStep = void 0;
const fp = require("fp-ts");
const t = require("io-ts");
const monocle = require("monocle-ts");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const ActionTypes = require("../../actionTypes/order/wizard");
const Actions = require("../../actions/order/wizard");
const _orderFiles = require("../../modules/orderFiles/epics");
const validation_1 = require("../../utils/validation");
const TypesLib = require("@caddigital/sok-types");
const errorHandling_1 = require("../../utils/errorHandling");
const orderFiles = _orderFiles.default('order');
//#region LENSES
const valuesLens = monocle.Lens.fromPath(['order', 'wizard', 'values']);
const activeStepLens = monocle.Lens.fromPath(['order', 'wizard', 'wizardStep']);
//#endregion
//#region Validation functions
/**
 * Validate billing informations
 */
const validateBillingInformation = (validation, x) => {
    if (x.billingInformation && x.billingInformation.type === 'CUSTOM') {
        return fp.function.compose((0, validation_1.ap)((0, validation_1.isNotUndefined)('billingInformation')(x.billingInformation)), (0, validation_1.ap)((0, validation_1.isNotEmpty)('customerName')(x.billingInformation)), (0, validation_1.ap)((0, validation_1.hasInvalidLength)('customerName', null, 150)(x.billingInformation.customerName)), fp.function.compose((0, validation_1.ap)((0, validation_1.isNotEmpty)('streetAddress')(x.billingInformation)), (0, validation_1.ap)((0, validation_1.hasInvalidLength)('streetAddress', null, 50)(x.billingInformation.streetAddress)), (0, validation_1.ap)((0, validation_1.isNotEmpty)('postalCode')(x.billingInformation)), (0, validation_1.ap)((0, validation_1.hasInvalidLength)('postalCode', null, 20)(x.billingInformation.postalCode)), (0, validation_1.ap)((0, validation_1.isNotEmpty)('city')(x.billingInformation)), (0, validation_1.ap)((0, validation_1.hasInvalidLength)('city', null, 50)(x.billingInformation.city))), (0, validation_1.ap)((0, validation_1.hasInvalidLength)('referenceNumber', 0, 40)(x.billingInformation.referenceNumber)))(validation);
    }
    else {
        if (x.billingInformation &&
            x.billingInformation.type === 'INTERNAL' &&
            x.billingInformation.projects &&
            x.billingInformation.projects.length > 0) {
            return fp.function.compose((0, validation_1.ap)((0, validation_1.isNotUndefined)('billingInformation')(x.billingInformation)), (0, validation_1.ap)((0, validation_1.isNotUndefined)('selectedProject')(x.billingInformation.selectedProject)))(validation);
        }
        else {
            return (0, validation_1.ap)((0, validation_1.isNotUndefined)('billingInformation')(x.billingInformation))(validation);
        }
    }
};
//#endregion
//#region Epics
/**
 * Validate step epic
 */
const validateStep = (step, mode, type) => (store) => {
    const currentStep = activeStepLens.get(store);
    const skipValidation = mode === 'VIEW';
    if (currentStep > step || skipValidation) {
        return fp.validation.success(true);
    }
    const isOrder = mode === 'CREATE' || mode === 'EDIT';
    const files = store.order.wizard.checkingFiles.files;
    let validationResponse = fp.validation.success(true);
    const values = valuesLens.get(store);
    // Order informations should be given and
    // invalid valid has been accepted
    if (step > 0) {
        const validation = (0, validation_1.ap)((0, validation_1.hasInvalidLength)('name', null, 130)(values.name))((0, validation_1.isNotEmpty)('name')(values));
        if (store.order.wizard.checkingFiles.status === 'INVALID' ||
            (type !== 'AUTOMATIC' &&
                store.order.wizard.checkingFiles.files.length === 0)) {
            validationResponse = (0, validation_1.ap)((0, validation_1.isNotFalse)('proceedWithoutInvalidFiles')(values))(validationResponse);
        }
        validationResponse = (0, validation_1.ap)(validateBillingInformation(validation, values))(validationResponse);
    }
    // if automatic mode then validate only the first step contents and files added from cart in step 3
    // Order should have atleast recipient
    if (isOrder && step > 1) {
        const recipients = Object.values(values.orderRecipients);
        const distributionListRecipients = values.distributionLists.reduce((acc, x) => {
            const total = x.recipients.filter((y) => !y.excluded);
            acc.push(...total);
            return acc;
        }, []);
        if (recipients.find((r) => r.deliveryMethod === -1)) {
            validationResponse = (0, validation_1.ap)(fp.validation.failure([
                {
                    key: 'recipients',
                    value: 'no deliverymethod selected'
                }
            ]))(validationResponse);
        }
        if (type === 'SINGLE') {
            // There should be atleast on recipient
            // There shoul be atleast one distribution list with one recipient
            if (recipients.length === 0 &&
                distributionListRecipients.length === 0) {
                validationResponse = (0, validation_1.ap)(fp.validation.failure([
                    {
                        key: 'recipients',
                        value: 'no repicipients'
                    }
                ]))(validationResponse);
            }
        }
    }
    // Order should have atleast one file
    if (step > 2 && isOrder && type !== 'AUTOMATIC') {
        if (files.length === 0) {
            validationResponse = (0, validation_1.ap)(fp.validation.failure([
                {
                    key: 'files',
                    value: 'no files'
                }
            ]))(validationResponse);
        }
    }
    return validationResponse;
};
exports.validateStep = validateStep;
/**
 * Change step epic
 */
function changeStep(action$, store) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.CHANGE_STEP), (0, operators_1.mergeMap)((request) => (0, exports.validateStep)(request.payload.wizardStep, request.payload.mode, request.payload.type)(store.value).fold((e) => (0, rxjs_1.of)(Actions.validateStepFailure(e)), (_) => (0, rxjs_1.of)(Actions.validateStepSuccess(request.payload.wizardStep)))));
}
exports.changeStep = changeStep;
/**
 *  Create order epic
 */
function createNewOrder(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.CREATE_NEW_ORDER_REQUEST), (0, operators_1.mergeMap)((request) => {
        const state = store.value;
        return (0, exports.validateStep)(5, 'CREATE', request.payload.type === 'AUTOMATIC_ORDER' &&
            request.payload.mode === 'SAVE'
            ? 'AUTOMATIC'
            : 'SINGLE')(state).fold((e) => (0, rxjs_1.of)(Actions.validateStepFailure(e)), () => {
            const wizard = state.order.wizard;
            const values = wizard.values;
            const currentFrame = wizard.fileHistory.currentFrame;
            const files = state.order.wizard.checkingFiles.files.reduce((acc, file) => {
                if (currentFrame.hardDeleted[file.uuid]) {
                    return acc;
                }
                if (currentFrame.colored[file.uuid] !== undefined) {
                    file.color = currentFrame.colored[file.uuid];
                }
                if (currentFrame.deleted[file.uuid] !== undefined) {
                    file.excluded = currentFrame.deleted[file.uuid];
                }
                else {
                    file.excluded = false;
                }
                if (file.excluded) {
                    const hasParent = state.order.wizard.checkingFiles.files.find((x) => x.id === file.parent);
                    // Ignore parentless file
                    if (hasParent) {
                        acc.push(file);
                    }
                }
                else {
                    acc.push(file);
                }
                return acc;
            }, []);
            const possibleRecipients = (state.order.recipients.list.status === 'FETCHED'
                ? state.order.recipients.list.users
                : []).concat(state.order.addressBook.list.status === 'FETCHED'
                ? state.order.addressBook.list.users
                : []);
            const recipients = Object.values(values.orderRecipients).reduce((arr, x) => {
                if (possibleRecipients.find((u) => u.uuid === x.user.uuid)) {
                    arr.push(x);
                }
                return arr;
            }, []);
            const distributionRecipients = values.distributionLists.reduce((arr, item) => {
                return item.recipients.reduce((recipients, x) => {
                    if (possibleRecipients.find((u) => u.uuid === x.user.uuid)) {
                        recipients.push(x);
                    }
                    return recipients;
                }, arr);
            }, []);
            if (!values.billingInformation) {
                return (0, rxjs_1.of)(Actions.validateStepFailure([
                    {
                        key: 'billingInformation',
                        value: 'missing'
                    }
                ]));
            }
            const order = Object.assign(Object.assign({}, request.payload), { files: files, name: values.name, requestTimeOfDelivery: values.requestTimeOfDelivery, additionalInformation: wizard.step4.additionalInformation, recipients: distributionRecipients.concat(recipients), billingInformation: values.billingInformation });
            // Automatic order
            if (order.type !== 'ORDER') {
                order.id = values.orderId;
            }
            return ajax({
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: getToken()
                },
                method: order.id ? 'PUT' : 'POST',
                body: order,
                url: `${baseUrl}api/order/${order.id || ''}`
            }).pipe((0, operators_1.mergeMap)(({ response }) => t
                .array(TypesLib.Order.iOrder)
                .decode(response)
                .fold((e) => (0, rxjs_1.of)(Actions.createNewOrderFailed((0, errorHandling_1.parseValidationError)(e))), (orders) => {
                const created = orders.find((x) => x.type === request.payload.type);
                if (!created) {
                    return (0, rxjs_1.of)(Actions.createNewOrderFailed({
                        type: 'UNKNOWN_ERROR',
                        raw: orders
                    }));
                }
                return (0, rxjs_1.of)(Actions.createNewOrderSuccess(created));
            })), (0, operators_1.catchError)((e) => (0, rxjs_1.of)(Actions.createNewOrderFailed((0, errorHandling_1.parseAjaxErrorResponse)(e)))));
        });
    }));
}
exports.createNewOrder = createNewOrder;
exports.prevalidateFiles = orderFiles.prevalidateFiles;
exports.prevalidateImportedFiles = orderFiles.prevalidateImportedFiles;
function fetchOrder(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_ORDER_REQUEST), (0, operators_1.mergeMap)((request) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/order/${request.payload.id}?mode=${request.payload.mode === 'EDIT' ? 'LATEST' : 'ORIGINAL'}`
    }).pipe((0, operators_1.mergeMap)(({ response }) => {
        return TypesLib.Order.iOrder
            .decode(response)
            .fold((e) => (0, rxjs_1.of)(Actions.fetchOrderFailure((0, errorHandling_1.parseValidationError)(e))), (res) => (0, rxjs_1.merge)([
            Actions.fetchOrderSuccess(res),
            Actions.fetchOrderFilesRequest(request.payload.id, request.payload.type === 'AUTOMATIC'
                ? 'EDIT'
                : request.payload.mode)
        ]));
    }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.fetchOrderFailure((0, errorHandling_1.parseAjaxErrorResponse)(error)))))));
}
exports.fetchOrder = fetchOrder;
function fetchOrderFiles(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_ORDER_FILES_REQUEST), (0, operators_1.mergeMap)((request) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/order/${request.payload.id}/files/${request.payload.mode === 'EDIT' ? 'LATEST' : 'ORIGINAL'}`
    }).pipe((0, operators_1.mergeMap)(({ response }) => {
        return t
            .array(TypesLib.Order.iOrderFile)
            .decode(response)
            .fold((e) => (0, rxjs_1.of)(Actions.fetchOrderFilesFailure((0, errorHandling_1.parseValidationError)(e))), (res) => (0, rxjs_1.of)(Actions.fetchOrderFilesSuccess(res)));
    }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.fetchOrderFilesFailure((0, errorHandling_1.parseAjaxErrorResponse)(error)))))));
}
exports.fetchOrderFiles = fetchOrderFiles;
function deleteAutomaticOrderTemplate(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.DELETE_ORDER), (0, operators_1.mergeMap)((request) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        body: { ids: [request.payload.id] },
        method: 'DELETE',
        url: `${baseUrl}api/order/`
    }).pipe((0, operators_1.mergeMap)((res) => {
        return t
            .array(TypesLib.Order.iOrder)
            .decode(res.response)
            .fold((e) => (0, rxjs_1.of)(Actions.deleteOrderFailure((0, errorHandling_1.parseValidationError)(e))), (res) => (0, rxjs_1.of)(Actions.deleteOrderSuccess(res)));
    }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.deleteOrderFailure((0, errorHandling_1.parseAjaxErrorResponse)(error)))))));
}
exports.deleteAutomaticOrderTemplate = deleteAutomaticOrderTemplate;
function fetchOrderRecipients(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_RECIPIENTS), (0, operators_1.mergeMap)((request) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/order/${request.payload.id}/recipients/${request.payload.mode === 'VIEW' ? 'ORIGINAL' : 'LATEST'}`
    }).pipe((0, operators_1.mergeMap)(({ response }) => {
        return t
            .array(TypesLib.Distribution.iDistributionRecipient)
            .decode(response)
            .fold((e) => (0, rxjs_1.of)(Actions.fetchRecipientsFailure((0, errorHandling_1.parseValidationError)(e))), (res) => (0, rxjs_1.of)(Actions.fetchRecipientsSuccess(res)));
    }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.fetchRecipientsFailure((0, errorHandling_1.parseAjaxErrorResponse)(error)))))));
}
exports.fetchOrderRecipients = fetchOrderRecipients;
//# sourceMappingURL=wizard.js.map